import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { mdiCheckDecagram } from "@mdi/js";
import Icon from "@mdi/react";
import * as Sentry from "@sentry/react";
import { Alert, Button, Checkbox, Input, Steps } from "antd";
import { observer } from "mobx-react-lite";

import { api } from "../../api";
import AuthCard from "../../components/auth/AuthCard";
import { loginStore } from "../../stores/loginStore";
import { signupStore } from "../../stores/signupStore";
import { userStore } from "../../stores/userStore";
import { usePageTitle } from "../../utils/usePageTitle";
import { usePendingAction } from "../../utils/usePendingAction";
import { validatePassword } from "../../utils/validatePassword";

import "./SignupDetailsPage1.css";

export const SignupDetailsPage2 = observer(function SignupDetailsPage2() {
  const { t } = useTranslation();
  usePageTitle(t("signup.details.passwordHeader"));
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [receiveNews, setReceiveNews] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [registerError, setRegisterError] = useState(false);
  const { pending: registering, run: runCreate } = usePendingAction();

  useEffect(() => {
    if (!signupStore.token || !signupStore.firstName) {
      navigate("/signup/0", { replace: true });
    }
  }, [navigate]);

  const passwordInvalid = submitted && password && !validatePassword(password);
  const passwordMismatch =
    submitted && password && confirmPassword && password !== confirmPassword;

  async function handleCreate() {
    setSubmitted(true);
    if (
      !password ||
      !validatePassword(password) ||
      !confirmPassword ||
      password !== confirmPassword ||
      !agreedToTerms
    )
      return;
    await runCreate(async () => {
      try {
        const result = await api.auth.register({
          first_name: signupStore.firstName,
          last_name: signupStore.lastName,
          password,
          country: signupStore.country!,
          token: signupStore.token,
          agreed_to_newsletter: receiveNews,
          phone: signupStore.phone || undefined,
          lang: navigator.language.split("-")[0],
        });
        if (result.ok) {
          const next = signupStore.next;
          signupStore.clear();
          loginStore.clear();
          userStore.clear();
          sessionStorage.setItem("justRegistered", "true");
          if (next) {
            window.location.href = next;
          } else {
            navigate("/profile", { replace: true });
          }
        } else {
          setRegisterError(true);
        }
      } catch (e) {
        Sentry.captureException(e);
        setRegisterError(true);
      }
    });
  }

  return (
    <AuthCard>
      <span className="signup-details__email-chip">
        <Icon color="#4caf50" path={mdiCheckDecagram} size={0.7} />
        {signupStore.email}
      </span>

      <p className="signup-details__verified">
        {t("signup.details.verifiedText")}
      </p>

      <Steps
        current={1}
        className="signup-details__steps"
        items={[
          { title: t("signup.details.stepInfo") },
          { title: t("signup.details.stepPassword") },
        ]}
      />

      <h1 className="signup-details__form-header">
        {t("signup.details.passwordHeader")}
      </h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleCreate();
        }}
      >
        <input
          type="text"
          name="email"
          autoComplete="username"
          value={signupStore.email}
          readOnly
          style={{ display: "none" }}
        />
        <Input.Password
          placeholder={t("signup.details.password")}
          size="large"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          status={
            submitted && (!password || passwordInvalid) ? "error" : undefined
          }
        />
        {submitted && !password && (
          <div className="signup-details__field-error">
            {t("signup.details.required")}
          </div>
        )}
        {passwordInvalid && (
          <div className="signup-details__field-error">
            {t("signup.details.passwordRequirements")}
          </div>
        )}

        <Input.Password
          placeholder={t("signup.details.confirmPassword")}
          size="large"
          style={{ marginTop: 12 }}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          status={
            submitted && (!confirmPassword || passwordMismatch)
              ? "error"
              : undefined
          }
        />
        {submitted && !confirmPassword && (
          <div className="signup-details__field-error">
            {t("signup.details.required")}
          </div>
        )}
        {passwordMismatch && (
          <div className="signup-details__field-error">
            {t("signup.details.passwordMismatch")}
          </div>
        )}

        <div className="signup-details__checkboxes">
          <div>
            <Checkbox
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
            >
              <Trans
                i18nKey="signup.details.agreeToTerms"
                components={{
                  termsLink: (
                    <a
                      href={t("signup.details.termsUrl")}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                  privacyLink: (
                    <a
                      href={t("signup.details.privacyUrl")}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              />
            </Checkbox>
            {submitted && !agreedToTerms && (
              <div className="signup-details__field-error signup-details__field-error--checkbox">
                {t("signup.details.agreeRequired")}
              </div>
            )}
          </div>

          <Checkbox
            checked={receiveNews}
            onChange={(e) => setReceiveNews(e.target.checked)}
          >
            <Trans
              i18nKey="signup.details.receiveNews"
              components={{
                nextgisLink: (
                  <a
                    href={t("signup.details.nextgisNewsUrl")}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </Checkbox>
        </div>

        {registerError && (
          <Alert
            type="error"
            style={{ marginTop: 20 }}
            className="signup-details__alert"
            classNames={{ description: "signup-details__alert-description" }}
            description={
              <Trans
                i18nKey="signup.details.registerError"
                components={{
                  restartLink: <Link to="/signup/0" />,
                }}
              />
            }
          />
        )}

        <Button
          type="primary"
          size="large"
          block
          style={{ marginTop: 24 }}
          onClick={handleCreate}
          loading={registering}
        >
          {t("signup.details.createAccount")}
        </Button>
      </form>
    </AuthCard>
  );
});
